/**
 * Horizontal pill strip for selecting / creating / renaming / deleting
 * the gameplan's lines.
 *
 * Per-pill controls collapse the old "active line action bar" into
 * the pill itself:
 *   - Pill shows the line name + node·edge counts inline.
 *   - Clicking an inactive pill activates that line.
 *   - Clicking the already-active pill opens an inline rename input
 *     right inside the pill — Enter / blur commits, Esc cancels.
 *   - Each pill grows a small × in its top-right corner on hover;
 *     clicking that × deletes the line (no separate confirm step —
 *     the action is local and easy to recreate).
 *
 * The whole strip is a horizontally-scrollable flex row so plans with
 * many lines stay navigable without wrapping.
 */
import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../state/store';
import type { Gameplan, Line } from '../types/gameplan';

type Props = {
  gameplan: Gameplan;
  activeLineId: string | null;
};

export function LineCarousel({ gameplan, activeLineId }: Props) {
  const setActiveLine = useAppStore((s) => s.setActiveLine);
  const createLine = useAppStore((s) => s.createLine);
  const renameLine = useAppStore((s) => s.renameLine);
  const deleteLine = useAppStore((s) => s.deleteLine);

  const stripRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLDivElement>(null);

  // When the user clicks an already-active pill we open inline rename
  // on that specific line. Only one rename can be open at a time.
  const [editingLineId, setEditingLineId] = useState<string | null>(null);

  // Scroll the active pill into view whenever it changes — keeps the
  // current focus visible if the strip overflows.
  useEffect(() => {
    if (!activeTabRef.current || !stripRef.current) return;
    activeTabRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }, [activeLineId]);

  // If the line being renamed disappears (e.g. another action removed
  // it), close the editor so the input doesn't dangle.
  useEffect(() => {
    if (
      editingLineId !== null &&
      !gameplan.lines.some((l) => l.id === editingLineId)
    ) {
      setEditingLineId(null);
    }
  }, [gameplan.lines, editingLineId]);

  const onCreate = () => {
    const id = createLine(gameplan.id, `Line ${gameplan.lines.length + 1}`);
    setActiveLine(id);
  };

  const onPillClick = (line: Line) => {
    if (line.id !== activeLineId) {
      setActiveLine(line.id);
      return;
    }
    // Already active — toggle into rename mode.
    setEditingLineId(line.id);
  };

  const onCommitRename = (line: Line, name: string) => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== line.name) {
      renameLine(gameplan.id, line.id, trimmed);
    }
    setEditingLineId(null);
  };

  const onCancelRename = () => setEditingLineId(null);

  const onDelete = (line: Line) => {
    deleteLine(gameplan.id, line.id);
    if (editingLineId === line.id) setEditingLineId(null);
  };

  return (
    <div
      ref={stripRef}
      style={{
        display: 'flex',
        gap: 6,
        overflowX: 'auto',
        // Balanced vertical padding so the pills' centre lines up with
        // the frozen play button beside the strip; still enough top room
        // that the × badge (which floats 6px above each pill) isn't
        // clipped by the implicit overflowY that `overflowX: auto` adds.
        padding: '8px 2px 8px 2px',
        scrollbarWidth: 'thin',
        // Keep the strip on one line — pills never wrap.
        flexWrap: 'nowrap',
        // Never let the flex column above compress the strip. When
        // the drawer / bottom-sheet gets short, the strip must keep
        // its full natural height (pill + × badge + padding) — the
        // canvas below (flex:1) sacrifices its depth instead. Without
        // this, a short drawer squeezed the strip's height and the
        // overflowX:auto clipped the pills' text vertically, which
        // read as broken. flexShrink:0 pins the strip and pushes the
        // loss to canvas depth (the preferred trade — you can scroll
        // the canvas, you can't un-clip a pill).
        flexShrink: 0,
      }}
    >
      {gameplan.lines.map((line) => (
        <LinePill
          key={line.id}
          line={line}
          active={line.id === activeLineId}
          editing={editingLineId === line.id}
          onClick={() => onPillClick(line)}
          onCommitRename={(name) => onCommitRename(line, name)}
          onCancelRename={onCancelRename}
          onDelete={() => onDelete(line)}
          pillRef={line.id === activeLineId ? activeTabRef : undefined}
        />
      ))}
      <button
        onClick={onCreate}
        title="New line"
        style={{
          background: 'rgba(155,231,255,0.10)',
          border: '1px dashed rgba(155,231,255,0.30)',
          color: '#cdf6ff',
          borderRadius: 14,
          padding: '4px 12px',
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
        onMouseEnter={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.background =
            'rgba(155,231,255,0.18)')
        }
        onMouseLeave={(e) =>
          ((e.currentTarget as HTMLButtonElement).style.background =
            'rgba(155,231,255,0.10)')
        }
      >
        + new
      </button>
    </div>
  );
}

// -----------------------------------------------------------------
// Pill
// -----------------------------------------------------------------

function LinePill({
  line,
  active,
  editing,
  onClick,
  onCommitRename,
  onCancelRename,
  onDelete,
  pillRef,
}: {
  line: Line;
  active: boolean;
  editing: boolean;
  onClick: () => void;
  onCommitRename: (name: string) => void;
  onCancelRename: () => void;
  onDelete: () => void;
  pillRef?: React.RefObject<HTMLDivElement>;
}) {
  const [hovered, setHovered] = useState(false);
  const [draft, setDraft] = useState(line.name);

  // Re-sync draft when entering edit mode or line name changes.
  useEffect(() => {
    if (editing) setDraft(line.name);
  }, [editing, line.name]);

  const nodes = line.nodes.length;
  const edges = line.edges.length;

  return (
    <div
      ref={pillRef}
      role="button"
      tabIndex={editing ? -1 : 0}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={editing ? undefined : onClick}
      onKeyDown={(e) => {
        if (editing) return;
        // Enter / Space on a focused pill routes through the same
        // onClick handler — for the active pill that means opening
        // the rename input, for an inactive one it activates first.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      title={line.name}
      style={{
        position: 'relative',
        background: active
          ? 'rgba(155,231,255,0.18)'
          : hovered
            ? 'rgba(255,255,255,0.06)'
            : 'rgba(255,255,255,0.03)',
        border: `1px solid ${
          active ? 'rgba(155,231,255,0.50)' : 'rgba(255,255,255,0.10)'
        }`,
        color: active ? '#cdf6ff' : '#cdd2da',
        borderRadius: 14,
        padding: '4px 14px 4px 12px',
        fontSize: 11,
        fontWeight: active ? 600 : 500,
        cursor: editing ? 'text' : 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        transition: 'background 100ms ease, border-color 100ms ease',
        // Subtle outline on keyboard focus so Enter-to-rename is
        // discoverable.
        outline: 'none',
      }}
      onFocus={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow =
          '0 0 0 2px rgba(155,231,255,0.30)';
      }}
      onBlur={(e) => {
        (e.currentTarget as HTMLDivElement).style.boxShadow = '';
      }}
    >
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onBlur={() => onCommitRename(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onCommitRename(draft);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              onCancelRename();
            }
            e.stopPropagation();
          }}
          style={{
            background: 'rgba(0,0,0,0.45)',
            border: '1px solid rgba(155,231,255,0.40)',
            borderRadius: 4,
            color: '#e6e8ef',
            fontSize: 11,
            padding: '2px 6px',
            outline: 'none',
            // Small min-width so the input doesn't collapse on empty
            // strings; otherwise width tracks the draft content.
            minWidth: 70,
          }}
        />
      ) : (
        <span
          style={{
            maxWidth: 160,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {line.name}
        </span>
      )}

      {/* Counts inline so the pill carries the same info the old
          action bar did. Subtle so they don't dominate the name. */}
      {!editing && (
        <span
          style={{
            fontSize: 9,
            opacity: 0.55,
            fontWeight: 500,
          }}
        >
          · {nodes}n · {edges}e
        </span>
      )}

      {/* Top-right × — visible only when the pill is hovered. */}
      {hovered && !editing && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete line"
          style={{
            position: 'absolute',
            top: -6,
            right: -6,
            width: 16,
            height: 16,
            borderRadius: 8,
            background: 'rgba(20,22,30,0.95)',
            border: '1px solid rgba(255,128,140,0.45)',
            color: '#ffaab2',
            fontSize: 10,
            fontWeight: 700,
            lineHeight: '14px',
            padding: 0,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.background =
              'rgba(255,128,140,0.20)')
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.background =
              'rgba(20,22,30,0.95)')
          }
        >
          ×
        </button>
      )}
    </div>
  );
}
